import { useTheme } from '@chakra-ui/react';
import styled from 'styled-components';
import { ItemMetaType } from '../../helpers/Investments.types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CustomTooltipContainer = styled.div`
  width: 200px;
  background-color: #fff;
  box-shadow: 0 1px 4px 1px rgba(0, 0, 0, 0.15);
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  padding: 8px;
  border-radius: 5px;
  font-family: 'Inter';
  font-size: '10px';
  position: fixed;
  z-index: 10;
  pointer-events: none;
  transition: transform 0.1s ease-out;
`;

type CustomTooltipLabelProps = {
  fontSize?: string;
  color?: string;
  mb?: string;
};

const CustomTooltipLabel = styled.p<CustomTooltipLabelProps>`
  font-size: ${({ fontSize }) => fontSize || '10px'};
  font-weight: 700;
  color: ${({ color }) => color || '#777'};
  margin-bottom: ${({ mb }) => mb || '0px'};
`;

export interface CustomTooltipProps {
  active: boolean;
  payload: PayloadType;
  label: string;
}

type PayloadType = {
  chartType: undefined | string;
  color: string;
  datakey: string;
  fill: string;
  formatter?: string | undefined;
  name: string;
  payload: ItemMetaType;
  type: undefined | string;
  unit: undefined | string;
  value: string;
};

const ColorDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background-color: ${({ color }) => color};
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const CustomTooltip = ({ active, payload, label }: any) => {
  const { colors } = useTheme();

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const requestRef = useRef<number>();

  useEffect(() => {
    let lastKnownPosition = { x: 0, y: 0 };

    const updateMousePosition = (e: MouseEvent) => {
      lastKnownPosition = { x: e.clientX, y: e.clientY };
    };

    const updateState = () => {
      setMousePosition(lastKnownPosition);
      requestRef.current = requestAnimationFrame(updateState);
    };

    requestRef.current = requestAnimationFrame(updateState);

    window.addEventListener('mousemove', updateMousePosition, {
      passive: true,
    });

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, []);

  if (active && payload && payload.length) {
    const tooltipContent = (
      <CustomTooltipContainer
        style={{
          transform: `translate(${mousePosition.x + 10}px, ${
            mousePosition.y - 40
          }px)`,
          left: 0,
          top: 0,
        }}
      >
        <CustomTooltipLabel
          color={colors.text.primary}
          fontSize="10px"
          mb="8px"
        >{`${label}`}</CustomTooltipLabel>
        {payload?.map((item: any, index: number) => (
          <LabelWrapper key={index}>
            <ColorDot color={item?.color} />
            <CustomTooltipLabel
              color={colors.text.secondary}
              fontSize="12px"
            >{`${item?.value}%  ${item?.name}`}</CustomTooltipLabel>
          </LabelWrapper>
        ))}
      </CustomTooltipContainer>
    );

    return createPortal(tooltipContent, document.body);
  }

  return null;
};

export default CustomTooltip;
